import React, { useCallback, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionActions from '@material-ui/core/AccordionActions';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { Box, FormControlLabel, TextField, Switch } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
  DatePicker,
} from '@material-ui/pickers';
import moment from 'moment';
import { toast } from 'react-toastify';
import { daysOfWeek, IDaysOfWeek } from '../../../../utils/constants';
import {
  IScheduleDays,
  IScheduleDTO,
} from '../../../../shared/interfaces/schedule-interfaces';
import { parseDaysOfWeek } from '../../../../utils/parseDaysOfWeek';
import { useUpdateSchedule } from '../../../../hooks/data/schedules';
import { ScheduleDeleteDialog } from '../ScheduleDeleteDialog';
import LoadingButton from '../../../../components/LoadingButton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(2),
    },
    button: {
      borderRadius: '50%',
      minWidth: '0',
      width: '30px',
      height: '30px',
      margin: 'auto',
    },
    timeField: {
      marginTop: theme.spacing(2),
    },
  }),
);

type IProps = {
  schedule: IScheduleDTO;
};

const ScheduleRow: React.FC<IProps> = ({ schedule }) => {
  const classes = useStyles();

  const updateSchedule = useUpdateSchedule({ scheduleId: schedule.id! });

  const [data, setData] = useState({
    ...schedule,
    start_date: moment(schedule.start_date, 'DD-MM-yyyy').toDate(),
    end_date: moment(schedule.end_date, 'DD-MM-yyyy').toDate(),
    start_time: moment(schedule.start_time, 'HH:mm:ss').toDate(),
    end_time: moment(schedule.end_time, 'HH:mm:ss').toDate(),
  });
  const [expanded, setExpanded] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleClose = useCallback(() => {
    setOpenDeleteDialog(false);
  }, []);

  const handleChangeAccordion = useCallback(
    (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded);
    },
    [],
  );

  const handleDateChange = useCallback(
    (name: string) => (date: Date | null) => {
      setData({
        ...data,
        [name]: date,
      });
    },
    [data],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const formData = {
        ...data,
        start_date: moment(data.start_date).format('DD-MM-yyyy'),
        end_date: moment(data.end_date).format('DD-MM-yyyy'),
        start_time: moment(data.start_time).format('HH:mm'),
        end_time: moment(data.end_time).format('HH:mm'),
      };

      await updateSchedule.mutateAsync(formData);

      setExpanded(false);
    } catch (err) {
      toast.error('Erro no cadastro de um horário');
    }
  }, [updateSchedule, data]);

  const handleToggleDay = useCallback(
    (day: keyof IScheduleDays) => () => {
      setData({
        ...data,
        [day]: !data[day],
      });
    },
    [data],
  );

  const handleChangeSwitchInput = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setData({ ...data, [event.target.name]: event.target.checked });
  };
  return (
    <>
      <div className={classes.root}>
        <Accordion expanded={expanded} onChange={handleChangeAccordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1c-content"
            id="panel1c-header"
          >
            <Box>
              <Typography style={{ fontSize: '1.3em' }}>
                {`${schedule.start_time.substring(
                  0,
                  5,
                )}hs a ${schedule.end_time.substring(0, 5)}hs`}
              </Typography>
              <Typography>{parseDaysOfWeek(schedule)}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box width="100%" display="flex" flexDirection="column">
              <TextField
                variant="outlined"
                name="label"
                type="text"
                label="Etiqueta"
                value={data.label}
                margin="normal"
                onChange={e =>
                  setData({
                    ...data,
                    label: e.target.value,
                    // eslint-disable-next-line prettier/prettier
              })}
              />
              <Box mt={2} mb={2} display="flex" maxWidth="400px" width="100%">
                {Object.keys(daysOfWeek).map(day => (
                  <Button
                    key={day}
                    className={classes.button}
                    onClick={handleToggleDay(day as keyof IDaysOfWeek)}
                    variant="contained"
                    color="primary"
                    style={
                      !data[day as keyof IDaysOfWeek]
                        ? {
                            background: 'transparent',
                          }
                        : {}
                    }
                  >
                    {daysOfWeek[day as keyof IDaysOfWeek][0]}
                  </Button>
                ))}
              </Box>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  margin="normal"
                  format="dd/MM/yyyy"
                  label="Data de início"
                  value={data.start_date}
                  onChange={handleDateChange('start_date')}
                />
                <DatePicker
                  margin="normal"
                  format="dd/MM/yyyy"
                  label="Data de Fim"
                  value={data.end_date}
                  onChange={handleDateChange('end_date')}
                />
                <KeyboardTimePicker
                  ampm={false}
                  margin="normal"
                  id="startTime"
                  label="Horário de Início"
                  value={data.start_time}
                  onChange={handleDateChange('start_time')}
                  KeyboardButtonProps={{
                    'aria-label': 'change time',
                  }}
                />
                <KeyboardTimePicker
                  ampm={false}
                  margin="normal"
                  id="endTime"
                  label="Horário de Término"
                  value={data.end_time}
                  onChange={handleDateChange('end_time')}
                  KeyboardButtonProps={{
                    'aria-label': 'change time',
                  }}
                />
              </MuiPickersUtilsProvider>
              <FormControlLabel
                // eslint-disable-next-line prettier/prettier
                control={(
                  <Switch
                    checked={data.available}
                    onChange={handleChangeSwitchInput}
                    name="available"
                    color="primary"
                  />
                  // eslint-disable-next-line prettier/prettier
            )}
                label="Disponível"
              />
              <Typography variant="caption">
                Se essa chave acima estiver ligada, o horário ficará disponível
                para atendimento. Caso contrário, o horário não ficará
                disponível para atendimento. O seu cliente nao poderá marcar no
                seu horário de indisponibilidade
              </Typography>
              {updateSchedule.status === 'error' && (
                <Typography color="error">
                  {updateSchedule.error?.response?.data?.non_field_errors?.join(
                    ', ',
                  )}
                </Typography>
              )}
            </Box>
          </AccordionDetails>
          <Divider />
          <AccordionActions>
            <Button
              size="small"
              onClick={() => setOpenDeleteDialog(true)}
              disabled={updateSchedule.isLoading}
            >
              Excluir
            </Button>
            <LoadingButton
              loading={updateSchedule.isLoading}
              size="small"
              color="primary"
              variant="contained"
              onClick={handleSubmit}
            >
              Salvar
            </LoadingButton>
          </AccordionActions>
        </Accordion>
      </div>
      <ScheduleDeleteDialog
        schedule={schedule}
        handleClose={handleClose}
        open={openDeleteDialog}
      />
    </>
  );
};

export { ScheduleRow };
